import { useMemo, useState } from "react";
import {
  useCreate,
  useDelete,
  useGetLocale,
  useList,
  useTranslate,
  useUpdate,
} from "@refinedev/core";
import {
  PlusCircleSolidIcon,
  Button,
  EnvelopeIcon,
  Field,
  Modal,
  SelectField,
  StatusBadge,
  Tabs,
  TextareaField,
  type Column,
} from "@fawkes/ui";

import { formatDateTime } from "../i18n/format";
import "./CommunicatieScreen.css";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// settings-communicatie: the Communicatie & contracten detail screen — the React migration of the
// Jmix `SettingsDashboardView.buildCommunicationDocumentsSection()` plus the standalone
// ReportTemplate/CrecheContract list views it linked to. Opened from the Settings dashboard
// communication tile (org + branch). Three real CRUD surfaces over the docasoft store:
//   • Contracten     → /api/communicatie/contracten     (creche_contract, read-only)
//   • Afdrukken      → /api/communicatie/afdrukken      (report_template, full CRUD)
//   • Email berichten→ /api/communicatie/email-berichten(report_setting, list + edit)
// In the branch context the tabs are Afdrukken + Mail lay-out; the latter is gated by the
// BRANCH_COMMUNICATION_LAYOUT_EDITABLE flag (parity with the Jmix tile) — surfaced read-only until a
// shelter is allowed to override the org layout.

const BRANCH_COMMUNICATION_LAYOUT_EDITABLE = false;

type ContextType = "organization" | "branch";

type Tab = "contracten" | "afdrukken" | "emailBerichten" | "mailLayout";

interface Afdruk {
  id: number;
  name: string;
  path: string;
  reportSettingId: number;
  rowIndex: number;
  emailSubject?: string | null;
  emailMessage?: string | null;
}

interface EmailBericht {
  id: number;
  name: string;
  emailSubject?: string | null;
  emailMessage?: string | null;
  emailDefaultRecipient?: string | null;
  optionEmail?: boolean | null;
  optionPrint?: boolean | null;
}

interface Contract {
  id: number;
  contractNummer?: number | null;
  aanmaakDatum?: string | null;
  contractPeriodeVan?: string | null;
  contractPeriodeTot?: string | null;
  contractOnbeperkt?: boolean | null;
  dagenPerWeek?: number | null;
  geannuleerd?: boolean | null;
  vestigingCode?: string | null;
}

const AFDRUK_RESOURCE = "communicatie/afdrukken";
const EMAIL_RESOURCE = "communicatie/email-berichten";
const CONTRACT_RESOURCE = "communicatie/contracten";

function tabsForContext(type: ContextType): Tab[] {
  return type === "branch" ? ["afdrukken", "mailLayout"] : ["contracten", "afdrukken", "emailBerichten"];
}

interface AfdrukDraft {
  id: number | null;
  name: string;
  path: string;
  reportSettingId: string;
  rowIndex: string;
  emailSubject: string;
  emailMessage: string;
}

interface EmailDraft {
  id: number;
  name: string;
  emailSubject: string;
  emailMessage: string;
  emailDefaultRecipient: string;
}

export function CommunicatieScreen({
  context = "organization",
  onBack,
}: {
  context?: ContextType;
  onBack?: () => void;
}) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "en";

  const tabs = tabsForContext(context);
  const [activeTab, setActiveTab] = useState<Tab>(tabs[0]);
  const [search, setSearch] = useState("");

  // Branch "Mail lay-out" is the read-only org afdrukken view unless the shelter may override it.
  const layoutReadOnly = activeTab === "mailLayout" && !BRANCH_COMMUNICATION_LAYOUT_EDITABLE;
  const afdrukTab = activeTab === "afdrukken" || activeTab === "mailLayout";

  const { data: afdrukData, isLoading: afdrukLoading } = useList<Afdruk>({
    resource: AFDRUK_RESOURCE,
    queryOptions: { enabled: afdrukTab },
  });
  const { data: emailData, isLoading: emailLoading } = useList<EmailBericht>({
    resource: EMAIL_RESOURCE,
    // The report-setting list doubles as the Afdrukken "parent" lookup, so fetch it for both tabs.
    queryOptions: { enabled: activeTab === "emailBerichten" || afdrukTab },
  });
  const { data: contractData, isLoading: contractLoading } = useList<Contract>({
    resource: CONTRACT_RESOURCE,
    queryOptions: { enabled: activeTab === "contracten" },
  });

  const { mutate: createAfdruk, isLoading: creatingAfdruk } = useCreate();
  const { mutate: updateAfdruk, isLoading: updatingAfdruk } = useUpdate();
  const { mutate: deleteAfdruk } = useDelete();
  const { mutate: updateEmail, isLoading: updatingEmail } = useUpdate();

  const [afdrukDraft, setAfdrukDraft] = useState<AfdrukDraft | null>(null);
  const [emailDraft, setEmailDraft] = useState<EmailDraft | null>(null);

  const query = search.trim().toLowerCase();
  const matches = (text: string | null | undefined) => !query || (text ?? "").toLowerCase().includes(query);

  // --- row sets (client-side name filter, parity with the Jmix search box) ----------------------
  const afdrukRows = useMemo(
    () => (afdrukData?.data ?? []).filter((a) => matches(a.name) || matches(a.path)),
    [afdrukData, query],
  );
  const emailRows = useMemo(
    () => (emailData?.data ?? []).filter((e) => matches(e.name) || matches(e.emailSubject)),
    [emailData, query],
  );
  const contractRows = useMemo(
    () => (contractData?.data ?? []).filter((c) => matches(c.vestigingCode) || matches(String(c.contractNummer ?? ""))),
    [contractData, query],
  );

  const reportSettingOptions = useMemo(
    () => (emailData?.data ?? []).map((e) => ({ value: String(e.id), label: e.name })),
    [emailData],
  );

  // --- afdruk add/edit --------------------------------------------------------------------------
  function openAfdrukCreate() {
    setAfdrukDraft({ id: null, name: "", path: "", reportSettingId: "", rowIndex: "0", emailSubject: "", emailMessage: "" });
  }
  function openAfdrukEdit(a: Afdruk) {
    setAfdrukDraft({
      id: a.id,
      name: a.name,
      path: a.path,
      reportSettingId: String(a.reportSettingId),
      rowIndex: String(a.rowIndex ?? 0),
      emailSubject: a.emailSubject ?? "",
      emailMessage: a.emailMessage ?? "",
    });
  }
  function saveAfdruk() {
    if (!afdrukDraft) return;
    const rowIndex = Number.parseInt(afdrukDraft.rowIndex, 10) || 0;
    const common = {
      name: afdrukDraft.name,
      path: afdrukDraft.path,
      rowIndex,
      emailSubject: afdrukDraft.emailSubject || null,
      emailMessage: afdrukDraft.emailMessage || null,
    };
    if (afdrukDraft.id == null) {
      createAfdruk(
        { resource: AFDRUK_RESOURCE, values: { ...common, reportSettingId: Number.parseInt(afdrukDraft.reportSettingId, 10) } },
        { onSuccess: () => setAfdrukDraft(null) },
      );
    } else {
      updateAfdruk(
        { resource: AFDRUK_RESOURCE, id: afdrukDraft.id, values: common },
        { onSuccess: () => setAfdrukDraft(null) },
      );
    }
  }
  const afdrukValid =
    afdrukDraft != null &&
    afdrukDraft.name.trim() !== "" &&
    afdrukDraft.path.trim() !== "" &&
    (afdrukDraft.id != null || afdrukDraft.reportSettingId !== "");

  // --- email edit -------------------------------------------------------------------------------
  function openEmailEdit(e: EmailBericht) {
    setEmailDraft({
      id: e.id,
      name: e.name,
      emailSubject: e.emailSubject ?? "",
      emailMessage: e.emailMessage ?? "",
      emailDefaultRecipient: e.emailDefaultRecipient ?? "",
    });
  }
  function saveEmail() {
    if (!emailDraft) return;
    updateEmail(
      {
        resource: EMAIL_RESOURCE,
        id: emailDraft.id,
        values: {
          name: emailDraft.name,
          emailSubject: emailDraft.emailSubject || null,
          emailMessage: emailDraft.emailMessage || null,
          emailDefaultRecipient: emailDraft.emailDefaultRecipient || null,
        },
      },
      { onSuccess: () => setEmailDraft(null) },
    );
  }

  // --- columns ----------------------------------------------------------------------------------
  const afdrukColumns: Column<Afdruk>[] = [
    { header: t("instellingen.communicatie.column.naam"), cell: (a) => a.name },
    { header: t("instellingen.communicatie.column.pad"), cell: (a) => a.path },
    { header: t("instellingen.communicatie.column.volgorde"), cell: (a) => a.rowIndex },
  ];
  if (!layoutReadOnly) {
    afdrukColumns.push({
      header: t("instellingen.communicatie.column.acties"),
      cell: (a) => (
        <span className="settings-communication-row-actions">
          <Button variant="secondary" onClick={() => openAfdrukEdit(a)}>
            {t("common.edit")}
          </Button>
          <Button
            variant="secondary"
            onClick={() => deleteAfdruk({ resource: AFDRUK_RESOURCE, id: a.id })}
          >
            {t("common.delete")}
          </Button>
        </span>
      ),
    });
  }

  const emailColumns: Column<EmailBericht>[] = [
    { header: t("instellingen.communicatie.column.naam"), cell: (e) => e.name },
    { header: t("instellingen.communicatie.column.onderwerp"), cell: (e) => e.emailSubject ?? "—" },
    { header: t("instellingen.communicatie.column.ontvanger"), cell: (e) => e.emailDefaultRecipient ?? "—" },
    {
      header: t("instellingen.communicatie.column.type"),
      cell: (e) =>
        e.optionEmail ? (
          <StatusBadge tone="success">{t("instellingen.communicatie.type.email")}</StatusBadge>
        ) : (
          <StatusBadge tone="neutral">{t("instellingen.communicatie.type.afdruk")}</StatusBadge>
        ),
    },
    {
      header: t("instellingen.communicatie.column.acties"),
      cell: (e) => (
        <span className="settings-communication-row-actions">
          <Button variant="secondary" onClick={() => openEmailEdit(e)}>
            {t("common.edit")}
          </Button>
        </span>
      ),
    },
  ];

  const contractColumns: Column<Contract>[] = [
    { header: t("instellingen.communicatie.column.nummer"), cell: (c) => c.contractNummer ?? "—" },
    { header: t("instellingen.communicatie.column.vestiging"), cell: (c) => c.vestigingCode ?? "—" },
    { header: t("instellingen.communicatie.column.dagenPerWeek"), cell: (c) => c.dagenPerWeek ?? "—" },
    {
      header: t("instellingen.communicatie.column.periode"),
      cell: (c) =>
        c.contractOnbeperkt
          ? t("instellingen.communicatie.periode.onbeperkt")
          : `${formatDateTime(c.contractPeriodeVan, locale)} – ${formatDateTime(c.contractPeriodeTot, locale)}`,
    },
    {
      header: t("instellingen.communicatie.column.status"),
      cell: (c) =>
        c.geannuleerd ? (
          <StatusBadge tone="danger">{t("instellingen.communicatie.status.geannuleerd")}</StatusBadge>
        ) : (
          <StatusBadge tone="success">{t("instellingen.communicatie.status.actief")}</StatusBadge>
        ),
    },
  ];

  const contextLabel =
    context === "branch"
      ? t("instellingen.communicatie.breadcrumb.vestiging")
      : t("instellingen.communicatie.breadcrumb.organisatie");

  const addButton =
    activeTab === "afdrukken" ? (
      <Button variant="primary" onClick={openAfdrukCreate}>
        <PlusCircleSolidIcon width={18} height={18} /> {t("instellingen.communicatie.add.afdruk")}
      </Button>
    ) : null;

  return (
    <div className="settings-view-root">
      <div className="settings-view-content">
        <div className="settings-communication-shell">
          <div className="settings-communication-breadcrumb">
            <EnvelopeIcon className="settings-communication-breadcrumb-icon" width={14} height={14} />
            <span>
              {t("instellingen.title")} › {contextLabel} › {t("instellingen.tile.communication.title")}
            </span>
          </div>

          <h3 className="settings-communication-title">{t("instellingen.tile.communication.title")}</h3>
          <p className="settings-communication-subtitle">{t("instellingen.communicatie.subtitle")}</p>

          <div className="settings-communication-tab-row">
            <Tabs
              tabs={tabs.map((tab) => ({ key: tab, label: t(`instellingen.item.${tab}`) }))}
              activeKey={activeTab}
              onChange={(key) => {
                setActiveTab(key as Tab);
                setSearch("");
              }}
            />
            {addButton}
          </div>

          <div className="settings-communication-filter-row">
            <div className="settings-communication-search">
              <Field
                label={t("instellingen.communicatie.search")}
                placeholder={t("instellingen.communicatie.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {layoutReadOnly && (
            <div className="settings-info-banner">{t("instellingen.communicatie.mailLayout.readOnly")}</div>
          )}

          {afdrukTab && (
            afdrukLoading ? (
              <p className="fawkes-muted">{t("common.loading")}</p>
            ) : (
              <IndeelbareTabel tabelKey="communicatie-screen-communicatie"
                columns={afdrukColumns}
                rows={afdrukRows}
                getRowKey={(a) => String(a.id)}
                empty={t("instellingen.communicatie.empty")}
              />
            )
          )}

          {activeTab === "emailBerichten" && (
            emailLoading ? (
              <p className="fawkes-muted">{t("common.loading")}</p>
            ) : (
              <IndeelbareTabel tabelKey="communicatie-screen-communicatie-2"
                columns={emailColumns}
                rows={emailRows}
                getRowKey={(e) => String(e.id)}
                empty={t("instellingen.communicatie.empty")}
              />
            )
          )}

          {activeTab === "contracten" && (
            contractLoading ? (
              <p className="fawkes-muted">{t("common.loading")}</p>
            ) : (
              <IndeelbareTabel tabelKey="communicatie-screen-communicatie-3"
                columns={contractColumns}
                rows={contractRows}
                getRowKey={(c) => String(c.id)}
                empty={t("instellingen.communicatie.empty")}
              />
            )
          )}

          {onBack && (
            <div className="settings-communication-footer">
              <Button variant="secondary" onClick={onBack}>
                {t("common.back")}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Afdruk add/edit */}
      <Modal
        open={afdrukDraft !== null}
        title={
          afdrukDraft?.id == null
            ? t("instellingen.communicatie.add.afdruk")
            : t("instellingen.communicatie.edit.afdruk")
        }
        onClose={() => setAfdrukDraft(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAfdrukDraft(null)}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" onClick={saveAfdruk} disabled={!afdrukValid || creatingAfdruk || updatingAfdruk}>
              {t("common.save")}
            </Button>
          </>
        }
      >
        {afdrukDraft && (
          <div className="settings-communication-form">
            <Field
              label={t("instellingen.communicatie.field.naam")}
              value={afdrukDraft.name}
              onChange={(e) => setAfdrukDraft({ ...afdrukDraft, name: e.target.value })}
            />
            <Field
              label={t("instellingen.communicatie.field.pad")}
              value={afdrukDraft.path}
              onChange={(e) => setAfdrukDraft({ ...afdrukDraft, path: e.target.value })}
            />
            {afdrukDraft.id == null && (
              <SelectField
                label={t("instellingen.communicatie.field.rapportinstelling")}
                options={[{ value: "", label: t("instellingen.communicatie.field.kies") }, ...reportSettingOptions]}
                value={afdrukDraft.reportSettingId}
                onChange={(e) => setAfdrukDraft({ ...afdrukDraft, reportSettingId: e.target.value })}
              />
            )}
            <Field
              label={t("instellingen.communicatie.field.volgorde")}
              type="number"
              value={afdrukDraft.rowIndex}
              onChange={(e) => setAfdrukDraft({ ...afdrukDraft, rowIndex: e.target.value })}
            />
          </div>
        )}
      </Modal>

      {/* Email edit */}
      <Modal
        open={emailDraft !== null}
        title={t("instellingen.communicatie.edit.email")}
        onClose={() => setEmailDraft(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEmailDraft(null)}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" onClick={saveEmail} disabled={emailDraft?.name.trim() === "" || updatingEmail}>
              {t("common.save")}
            </Button>
          </>
        }
      >
        {emailDraft && (
          <div className="settings-communication-form">
            <Field
              label={t("instellingen.communicatie.field.naam")}
              value={emailDraft.name}
              onChange={(e) => setEmailDraft({ ...emailDraft, name: e.target.value })}
            />
            <Field
              label={t("instellingen.communicatie.field.onderwerp")}
              value={emailDraft.emailSubject}
              onChange={(e) => setEmailDraft({ ...emailDraft, emailSubject: e.target.value })}
            />
            <Field
              label={t("instellingen.communicatie.field.ontvanger")}
              type="email"
              invalidEmailMessage={t("validatie.emailadres")}
              value={emailDraft.emailDefaultRecipient}
              onChange={(e) => setEmailDraft({ ...emailDraft, emailDefaultRecipient: e.target.value })}
            />
            <TextareaField
              label={t("instellingen.communicatie.field.bericht")}
              value={emailDraft.emailMessage}
              onChange={(e) => setEmailDraft({ ...emailDraft, emailMessage: e.target.value })}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
